import React, { useState, useEffect } from "react";
import { Table, Select, Tag, Button, Card, Badge, Tooltip, Spin } from "antd";
import { EyeOutlined, UserOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { getSegments, getSegmentUsers, previewSegmentUsers } from "../../../hooks/segmentApi";

const { Option } = Select;

const SegmentSelector = ({
  selectedSegments = [],
  onSegmentChange,
  tenantEmail,
}) => {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [previewSegmentId, setPreviewSegmentId] = useState(null);

  useEffect(() => {
    loadSegments();
  }, []);

  const loadSegments = async () => {
    try {
      setLoading(true);
      const res = await getSegments(tenantEmail);
      if (res.data && Array.isArray(res.data.data)) {
        console.log(res.data.data);
        
        setSegments(res.data.data);
      } else if (Array.isArray(res.data)) {
        setSegments(res.data);
      }
    } catch (error) {
      console.error("Failed to load segments:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (segmentId) => {
    try {
      setPreviewLoading(true);
      setPreviewSegmentId(segmentId);
      const res = await previewSegmentUsers(segmentId, tenantEmail);
      
      if (res.data && res.data.data && Array.isArray(res.data.data.users)) {
        setPreviewData(res.data.data.users);
      } else if (Array.isArray(res.data.data)) {
        setPreviewData(res.data.data);
      } else {
        setPreviewData([]);
      }
    } catch (error) {
      console.error("Failed to preview segment:", error);
      setPreviewData([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSegmentSelect = (value) => {
    if (value && !selectedSegments.includes(value)) {
      onSegmentChange([...selectedSegments, value]);
    }
  };

  const handleRemoveSegment = (segmentId) => {
    onSegmentChange(selectedSegments.filter(id => id !== segmentId));
    if (previewSegmentId === segmentId) {
      setPreviewData([]);
      setPreviewSegmentId(null);
    }
  };

  const getSegmentById = (id) => segments.find(s => s._id === id);
  
  const selectedSegmentObjects = selectedSegments
    .map(id => getSegmentById(id))
    .filter(Boolean);

  const columns = [
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
    },
    {
      title: 'Name',
      dataIndex: 'userName',
      key: 'userName',
    },
    {
      title: 'Phone',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
    },
    {
      title: 'Email',
      dataIndex: 'emailId',
      key: 'emailId',
    },
  ];

  return (
    <div className="segment-selector">
      <div className="mb-4">
        <label className="form-label fw-semibold mb-3">
          Select Segments
          <span className="text-muted ms-2">
            (Selected: {selectedSegments.length})
          </span>
        </label>
        
        <div className="d-flex align-items-start gap-3">
          <Select
            style={{ width: '100%', maxWidth: '400px' }}
            placeholder="Search and select segments..."
            onChange={handleSegmentSelect}
            loading={loading}
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {segments
              .filter(segment => !selectedSegments.includes(segment._id))
              .map(segment => (
                <Option key={segment._id} value={segment._id}>
                  <div className="d-flex justify-content-between">
                    <span>{segment.name}</span>
                    <span className="text-muted ms-2">
                      <UserOutlined /> {segment.userCount || 0}
                    </span>
                  </div>
                </Option>
              ))}
          </Select>
          
          <Button 
            type="primary" 
            onClick={loadSegments}
            loading={loading}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Selected Segments */}
      {selectedSegmentObjects.length > 0 && (
        <div className="mb-4">
          <h6 className="fw-semibold mb-3">Selected Segments</h6>
          <div className="row g-3">
            {selectedSegmentObjects.map(segment => (
              <div key={segment._id} className="col-md-6">
                <Card size="small" className="border">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="mb-1">
                        {segment.name}
                        {segment.active && (
                          <Badge 
                            count="Active" 
                            style={{ backgroundColor: '#52c41a', marginLeft: 8 }}
                          />
                        )}
                      </h6>
                      <p className="text-muted mb-1 small">
                        {segment.description || 'No description'}
                      </p>
                      <div className="d-flex align-items-center gap-2">
                        <Tag color="blue">
                          <UserOutlined /> {segment.userCount || 0} users
                        </Tag>
                        <Tag color="purple">
                          {segment.rules?.length || 0} rules
                        </Tag>
                        <span className="text-muted small">
                          Created: {new Date(segment.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    
                    <div className="d-flex gap-1">
                      <Tooltip title="Preview users">
                        <Button
                          type="text"
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={() => handlePreview(segment._id)}
                          loading={previewLoading && previewSegmentId === segment._id}
                        />
                      </Tooltip>
                      <Tooltip title="Remove">
                        <Button
                          type="text"
                          danger
                          size="small"
                          onClick={() => handleRemoveSegment(segment._id)}
                        >
                          ×
                        </Button>
                      </Tooltip>
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Segment Preview */}
      {previewData.length > 0 && (
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="fw-semibold mb-0">
              Preview: {getSegmentById(previewSegmentId)?.name}
              <span className="text-muted ms-2">
                ({previewData.length} users)
              </span>
            </h6>
            <Button 
              type="link" 
              size="small"
              onClick={() => {
                setPreviewData([]);
                setPreviewSegmentId(null);
              }}
            >
              Close Preview
            </Button>
          </div>
          
          <div style={{ maxHeight: '300px', overflow: 'auto' }}>
            <Table
              columns={columns}
              dataSource={previewData}
              rowKey="_id"
              size="small"
              pagination={false}
              loading={previewLoading}
            />
          </div>
        </div>
      )}

      {/* Empty State */}
      {segments.length === 0 && !loading && (
        <div className="text-center py-4 border rounded">
          <UserOutlined className="fs-1 text-muted mb-3" />
          <h6>No segments found</h6>
          <p className="text-muted small mb-0">
            Create segments first from the Segments page
          </p>
        </div>
      )}
    </div>
  );
};

export default SegmentSelector;